import { ChevronDown } from "lucide-react";
import type { TextCardGridComponent } from "../../types";
import { RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";

const COLS: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

function toPerRow(v: unknown): 2 | 3 | 4 {
  const n = Number(v);
  return n >= 2 && n <= 4 ? (n as 2 | 3 | 4) : 3;
}

/** Text card grid renderer on blue background with optional links. */
export function TextCardGridRenderer({ component }: { component: TextCardGridComponent }) {
  const { items, cardsPerRowTablet, cardsPerRowDesktop } = component.content;
  const tablet = toPerRow(cardsPerRowTablet);
  const desktop = toPerRow(cardsPerRowDesktop);
  const gridCols = `sm:grid-cols-2 md:${COLS[tablet]} lg:${COLS[desktop]}`;

  return (
    <section>
      <div className={`grid gap-4 ${gridCols}`}>
        {items.map((item, idx) => {
          const href = item.href ? resolveLinkHref(item.href) : null;
          const content = (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <span className="h-0.5 w-10 rounded-full bg-[#00B862] transition-all duration-300 group-hover:w-16" aria-hidden="true" />
              <h3 className="mt-3 text-base font-semibold text-[#0A0A0A] xl:text-lg">{item.title}</h3>
              <p className="mt-2 text-sm leading-6 text-[var(--color-muted)] xl:text-base">{item.text}</p>
              <span className="mt-3 flex shrink-0 text-[#00B862]" aria-hidden>
                <ChevronDown className="h-5 w-5" />
              </span>
            </div>
          );
          const card = (
            <div
              key={item.title + String(idx)}
              className="apa-card apa-card-hover group flex h-full min-h-0 flex-col items-center justify-center p-6 md:p-7"
            >
              {content}
            </div>
          );
          if (href) {
            return (
              <RuntimeLink key={item.title + String(idx)} href={href} className="block h-full">
                {card}
              </RuntimeLink>
            );
          }
          return card;
        })}
      </div>
    </section>
  );
}
